import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { errorMessage } from '../services/apiError';
import { replyToProjectInvitation, type InvitationReply } from '../services/projectInvitationService';

import '../styles/appModal.css';
import './PendingInviteModal.css';

/** What the window needs of a project listed as "Invite pending". */
export interface PendingProject {
  id: string;
  title?: string | null;
  role: string;
  access_status?: string;
  access_source?: string | null;
}

const accessWord = (role: string): string => {
  if (role === 'writer') return 'edit';

  return role === 'reader' ? 'view' : role;
};

const useReply = (projectId: string, onAccepted: (projectId: string) => void, onDeclined: () => void) => {
  const [busy, setBusy] = useState<InvitationReply | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reply = (answer: InvitationReply) => {
    setBusy(answer);
    setError(null);
    void replyToProjectInvitation(projectId, answer)
      .then(() => (answer === 'accept' ? onAccepted(projectId) : onDeclined()))
      .catch((err) => {
        setBusy(null);
        setError(errorMessage(err, 'That did not work. Try again.'));
      });
  };

  return { busy, error, reply };
};

const useEscape = (onClose: () => void) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
};

interface PendingInviteModalProps {
  project: PendingProject;
  onClose: () => void;
  onAccepted: (projectId: string) => void;
  onDeclined: () => void;
}

/** Accept or decline the invite to a project listed as "Invite pending". */
export const PendingInviteModal: React.FC<PendingInviteModalProps> = ({ project, onClose, onAccepted, onDeclined }) => {
  const { busy, error, reply } = useReply(project.id, onAccepted, onDeclined);
  const viaOrganization = project.access_source === 'organization_invitation';

  useEscape(onClose);

  return createPortal(
    // Clicks stop here: the window belongs to a card whose own click would open it again.
    <div className="am-overlay" onClick={(event) => { event.stopPropagation(); onClose(); }}>
      <div aria-labelledby="pending-invite-title" aria-modal="true" className="am-modal pending-invite"
        onClick={(event) => event.stopPropagation()} role="dialog">
        <button aria-label="Close" className="am-close" onClick={onClose} type="button">✕</button>
        <div className="am-header">
          <p className="am-eyebrow">Project invite</p>
          <h3 className="am-title" id="pending-invite-title">Join {project.title || 'this project'}?</h3>
          <p className="am-lede">
            {viaOrganization
              ? 'You are invited to the team that owns this project. Accept to join the team and open the project.'
              : `You are invited to this project with ${accessWord(project.role)} access.`}
          </p>
        </div>
        {error && <p className="am-error" role="alert">{error}</p>}
        <div className="am-actions">
          <button className="am-secondary" disabled={busy !== null} onClick={() => reply('decline')} type="button">
            {busy === 'decline' ? 'Declining…' : 'Decline'}
          </button>
          <button className="am-cta" disabled={busy !== null} onClick={() => reply('accept')} type="button">
            {busy === 'accept' ? 'Accepting…' : 'Accept'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * For a project card or row: a click opens the project or, while its invite is pending, this window. Accepting opens
 * the project; declining hides the card.
 */
export const usePendingInvite = (project: PendingProject, open: (projectId: string) => void) => {
  const [replying, setReplying] = useState(false);
  const [declined, setDeclined] = useState(false);
  const isPending = project.access_status === 'pending';
  const onActivate = () => (isPending ? setReplying(true) : open(project.id));
  const replyWindow = replying ? (
    <PendingInviteModal onAccepted={open} onClose={() => setReplying(false)}
      onDeclined={() => { setReplying(false); setDeclined(true); }} project={project} />
  ) : null;

  return { declined, isPending, onActivate, replyWindow };
};
